import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, CheckCircle, Circle, Clock, Trash, Warning } from "@phosphor-icons/react";
import api from "@/lib/api";
import { fdate } from "@/lib/format";
import { PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTranslation } from 'react-i18next'

const PRIORITY = { high: "text-red-600 bg-red-50 border-red-200", medium: "text-amber-600 bg-amber-50 border-amber-200", low: "text-slate-500 bg-slate-50 border-slate-200" };

export default function Tasks() {
  const { t } = useTranslation()
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: "", task_type: "Call client", priority: "medium", due_date: "", status: "pending" });

  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: async () => (await api.get("/meta")).data });
  const { data: tasks = [], isLoading } = useQuery({ queryKey: ["tasks"], queryFn: async () => (await api.get("/tasks")).data });

  useEffect(() => { if (params.get("new") === "1") { setShowForm(true); setParams({}); } }, [params, setParams]);

  const create = useMutation({
    mutationFn: async (payload) => (await api.post("/tasks", payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["tasks"] }); setShowForm(false); toast.success("Task created"); },
  });
  const toggle = useMutation({
    mutationFn: async ({ id, status }) => (await api.put(`/tasks/${id}`, { status })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tasks"] }),
  });
  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/tasks/${id}`)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["tasks"] });
      if (data?.pending_approval) toast.info(t("perm.deleteRequested", "Deletion request sent for Admin approval."));
    },
    onError: () => toast.error(t("actionFailed", "Action failed")),
  });

  const today = new Date().toISOString().slice(0, 10);
  const dueParam = params.get("due");
  const typeParam = (params.get("type") || "").toLowerCase();
  const filtered = tasks.filter((t) => {
    if (dueParam === "today" && !(t.due_date && t.due_date.slice(0, 10) === today)) return false;
    if (typeParam && !(t.task_type || "").toLowerCase().includes(typeParam)) return false;
    return true;
  });
  const activeFilter = dueParam === "today" ? t('followupsToday', 'Follow-ups Today')
    : typeParam ? t('upcomingVisits', 'Upcoming Visits') : null;
  const pending = filtered.filter((t) => t.status !== "done");
  const done = filtered.filter((t) => t.status === "done");

  const Row = ({ t }) => {
    const overdue = t.status !== "done" && t.due_date && t.due_date.slice(0, 10) < today;
    return (
      <div data-testid={`task-${t.id}`} className="flex items-center gap-3 bg-white border border-slate-200 rounded-sm px-4 py-3 hover:border-slate-300 group">
        <button onClick={() => toggle.mutate({ id: t.id, status: t.status === "done" ? "pending" : "done" })} data-testid={`task-toggle-${t.id}`}>
          {t.status === "done" ? <CheckCircle size={22} weight="fill" className="text-emerald-500" /> : <Circle size={22} className="text-slate-300 hover:text-[#C5A059]" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium ${t.status === "done" ? "line-through text-slate-400" : "text-slate-900"}`}>{t.title}</div>
          <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
            <span>{t.task_type}</span>
            {t.due_date && <span className={`flex items-center gap-1 ${overdue ? "text-red-500 font-medium" : ""}`}>{overdue ? <Warning size={12} /> : <Clock size={12} />} {fdate(t.due_date)}</span>}
          </div>
        </div>
        <span className={`text-[10px] uppercase px-2 py-0.5 border rounded-sm ${PRIORITY[t.priority] || PRIORITY.low}`}>{t.priority}</span>
        <button onClick={() => remove.mutate(t.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-red-500"><Trash size={16} /></button>
      </div>
    );
  };

  return (
    <div>
      <PageHeader
        title={t('tasksFollowups', 'Tasks & Follow-ups')}
        subtitle={t('lengthPendingLength2Completed', '{{length}} pending · {{length2}} completed', { length: pending.length, length2: done.length })}
        actions={
          <Button data-testid="add-task-btn" onClick={() => setShowForm(true)} className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white">
            <Plus size={16} className="mr-1.5" /> {t('addTask', 'Add Task')}
          </Button>
        }
      />
      {activeFilter && (
        <div className="mb-4 flex items-center gap-2" data-testid="tasks-filter-chip">
          <span className="text-xs px-2 py-1 rounded-sm bg-[#C5A059]/15 text-[#8a6d2f]">{activeFilter}</span>
          <button onClick={() => setParams({})} data-testid="tasks-filter-clear" className="text-xs text-[#C5A059] hover:underline">{t('clearFilter', 'Clear filter')}</button>
        </div>
      )}
      {isLoading ? (
        <div className="flex justify-center py-20"><div className="animate-spin h-8 w-8 border-2 border-[#0B2545] border-t-transparent rounded-full" /></div>
      ) : tasks.length === 0 ? (
        <EmptyState icon={CheckCircle} title={t('noTasksYet', 'No tasks yet')} subtitle="Create reminders and follow-ups." />
      ) : (
        <div className="space-y-6 max-w-3xl">
          <div className="space-y-2">
            <h3 className="text-xs uppercase tracking-wide text-slate-400">{t('pending', 'Pending')}</h3>
            {pending.length === 0 ? <p className="text-slate-400 text-sm">{t('allCaughtUp', 'All caught up.')}</p> : pending.map((t) => <Row key={t.id} t={t} />)}
          </div>
          {done.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs uppercase tracking-wide text-slate-400">{t('completed', 'Completed')}</h3>
              {done.map((t) => <Row key={t.id} t={t} />)}
            </div>
          )}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-md bg-white rounded-sm">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{t('addTask', 'Add Task')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>{t('title', 'Title *')}</Label><Input data-testid="task-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 rounded-sm" /></div>
            <div>
              <Label>{t("type", "Type")}</Label>
              <Select value={form.task_type} onValueChange={(v) => setForm({ ...form, task_type: v })}>
                <SelectTrigger className="mt-1 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">{meta?.task_types?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('priority', 'Priority')}</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="mt-1 rounded-sm bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white"><SelectItem value="high">{t('high', 'High')}</SelectItem><SelectItem value="medium">{t('medium', 'Medium')}</SelectItem><SelectItem value="low">{t('low', 'Low')}</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>{t('dueDate', 'Due Date')}</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="mt-1 rounded-sm" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setShowForm(false)}>{t('cancel', 'Cancel')}</Button>
            <Button data-testid="task-save" disabled={!form.title} onClick={() => create.mutate(form)} className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white">{t('create', 'Create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
